import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const MINEPORTAL_HERO_PRODUCT_ASSET =
  "/assets/products/mineportal/hero/itaas-mineportal-product-dashboard.png";
export const MINEPORTAL_HERO_HUMAN_ASSET =
  "/assets/products/mineportal/hero/itaas-mineportal-mining-engineer.png?v=1";

/** Editorial grounded in catalog SoT — hero mission does not rewrite product copy. */
export const MINEPORTAL_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "coo",
    icon: "🏛️",
    titleFr: "Direction / COO",
    titleEn: "Leadership / COO",
    bodyFr: "Consolidez sites, magasins et sous-traitance dans une vue exécutive multi-sites.",
    bodyEn: "Consolidate sites, stores and subcontracting in one multi-site executive view.",
  },
  {
    id: "ops",
    icon: "⛏️",
    titleFr: "Opérations minières",
    titleEn: "Mining operations",
    bodyFr: "Pilotez pits, centres de coût et activité terrain depuis le portail compagnie.",
    bodyEn: "Steer pits, cost centers and field activity from the company portal.",
  },
  {
    id: "maintenance",
    icon: "🔧",
    titleFr: "Maintenance",
    titleEn: "Maintenance",
    bodyFr: "Planifiez PM et correctifs multi-pits reliés au magasin ISRM.",
    bodyEn: "Plan PM and corrective work across pits linked to the ISRM store.",
  },
  {
    id: "store",
    icon: "📦",
    titleFr: "Magasin ISRM",
    titleEn: "ISRM store",
    bodyFr: "Reliez réquisitions, délivrance pièces et alertes stock aux ordres de travail.",
    bodyEn: "Connect requisitions, parts issue and stock alerts to work orders.",
  },
  {
    id: "arsp",
    icon: "🤝",
    titleFr: "Sous-traitance",
    titleEn: "Subcontracting",
    bodyFr: "Suivez registre, contrats et performance sous-traitants (ARSP / SubPortal).",
    bodyEn: "Track subcontractor registry, contracts and performance (ARSP / SubPortal).",
  },
];

export const MINEPORTAL_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "unified",
    icon: "🔗",
    titleFr: "Vue unifiée",
    titleEn: "Unified view",
    bodyFr: "Site → magasin → achats → sous-traitant dans un même fil opérationnel.",
    bodyEn: "Site → store → purchasing → subcontractor in one operational thread.",
  },
  {
    id: "compose",
    icon: "🧩",
    titleFr: "Composition ITaaS",
    titleEn: "ITaaS composition",
    bodyFr: "Réutilise Inventory, RH, AutoID et Billing sans dupliquer stock ni facturation.",
    bodyEn: "Reuses Inventory, HR, AutoID and Billing without duplicating stock or invoicing.",
  },
  {
    id: "arsp",
    icon: "📋",
    titleFr: "Sous-traitance structurée",
    titleEn: "Structured subcontracting",
    bodyFr: "Conformité ARSP intégrée au quotidien minier, fédérée avec SubPortal.",
    bodyEn: "ARSP compliance built into daily mining ops, federated with SubPortal.",
  },
  {
    id: "context",
    icon: "🗺️",
    titleFr: "Contextualisé RDC",
    titleEn: "DRC-contextualized",
    bodyFr: "Pensé pour Grand Katanga / Lualaba et les opérateurs cuivre-cobalt.",
    bodyEn: "Built for Greater Katanga / Lualaba and copper-cobalt operators.",
  },
];

/** Fallback if catalog featureGroups are empty after scrub — mirrors catalog groups. */
export const MINEPORTAL_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Organisation & sites",
    titleEn: "Organization & sites",
    features: [
      "Tableau de bord exécutif multi-sites",
      "Organisation sites, pits et centres de coût",
      "Mobilisation personnel FIFO",
    ],
  },
  {
    titleFr: "Sous-traitance ARSP",
    titleEn: "ARSP subcontracting",
    features: [
      "Registre sous-traitants et conformité ARSP",
      "Contrats et lots sous-traitance",
      "Fédération SubPortal",
    ],
  },
  {
    titleFr: "Magasin & maintenance",
    titleEn: "Store & maintenance",
    features: [
      "Magasin ISRM et réquisitions",
      "Planning maintenance et ordres de travail",
      "Scan code-barres (AutoID)",
    ],
  },
  {
    titleFr: "Suite & validations",
    titleEn: "Suite & approvals",
    features: [
      "Composition Inventory, RH, AutoID, Billing",
      "Moteur de validations personnalisables",
      "Achats MRO reliés aux sites",
    ],
  },
];

const MINEPORTAL_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "suite",
    labelFr: "Portail opérationnel Suite ITaaS",
    labelEn: "ITaaS Suite operational portal",
  },
  {
    id: "compose",
    labelFr: "Composé sur Inventory · RH · Billing",
    labelEn: "Composed on Inventory · HR · Billing",
  },
  {
    id: "local",
    labelFr: "Contextualisé RDC / Grand Katanga",
    labelEn: "Contextualized for DRC / Greater Katanga",
  },
];

export const MINEPORTAL_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: MINEPORTAL_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS MinePortal — opérations minières multi-sites",
    en: "ITaaS MinePortal dashboard — multi-site mining operations",
  },
  humanImageSrc: MINEPORTAL_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Ingénieur minier consultant une tablette avec carottes de forage",
    en: "Mining engineer reviewing a tablet with core samples",
  },
  visualCaption: {
    fr: "Interface produit ITaaS MinePortal — pilotage des opérations minières",
    en: "ITaaS MinePortal product interface — mining operations control",
  },
  audiences: MINEPORTAL_AUDIENCES,
  audienceTitle: {
    fr: "Un portail pour chaque acteur de l’exploitation minière",
    en: "A portal for every stakeholder in mining operations",
  },
  outcomes: MINEPORTAL_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour superviser sites, maintenance et sous-traitance.",
    en: "Practical benefits for supervising sites, maintenance and subcontracting.",
  },
  capabilityGroups: MINEPORTAL_CAPABILITY_GROUPS,
  preferConfigCapabilities: false,
  contextTitle: {
    fr: "Bind opérationnel pour opérateurs miniers",
    en: "Operational bind for mining operators",
  },
  contextLead: {
    fr: "Conçu pour le quotidien minier en RDC — sans promesse HSE avancée tant qu’elle reste en roadmap.",
    en: "Built for day-to-day mining in the DRC — without claiming advanced HSE while it remains on the roadmap.",
  },
  contextFallbackPoints: [
    {
      fr: "Vue unifiée site → magasin → achats → sous-traitant",
      en: "Unified view site → store → purchasing → subcontractor",
    },
    {
      fr: "Composition Inventory, RH, AutoID et Billing",
      en: "Composition of Inventory, HR, AutoID and Billing",
    },
    {
      fr: "Sous-traitance ARSP fédérée avec SubPortal",
      en: "ARSP subcontracting federated with SubPortal",
    },
  ],
  trustBaseChips: MINEPORTAL_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
};
